import { styled } from '@mui/material';
import MUITypography from '@mui/material/Typography';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FullscreenLoadingSpinner } from 'src/components/Loading/FullscreenLoadingSpinner';
import { Toast, useTrigger } from 'src/components/Toast';
import { usePost } from 'src/hooks/usePost';
import { TenantContext } from 'src/providers/Tenant';
import { useUser } from 'src/providers/User';

export default function ConfirmAccountPage({
  triggerSuccessToast,
}: Readonly<{
  triggerSuccessToast: (title: string, message: string) => void;
}>) {
  const { confirmSignup } = useUser();
  const { accountId } = useContext(TenantContext);

  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location?.search);
  const email = searchParams.get('email');
  const code = searchParams.get('code');

  const [errorToast, setErrorToast] = useTrigger();
  const [errorToastMessage, setErrorToastMessage] = useState<string | undefined>();

  const { triggerPostRequest: triggerSignupConfirmation } = usePost({
    handler: (body: { email: string; code: string }) => {
      const { email, code } = body;
      return confirmSignup(email, code);
    },
    onSuccess: () => {
      triggerSuccessToast(
        'Account activated',
        'Your account has been succesfully activated. Please login to enjoy the platform!'
      );

      history.push('/product-library');
    },
    onError: /* istanbul ignore next */ (error) => {
      if (error.name === 'ExpiredCodeException') {
        showErrorToast('Error confirming your account. Please request a new code');
      } else {
        showErrorToast(error.message);
      }
    },
  });
  /* istanbul ignore next */
  const showErrorToast = useCallback((message: string) => {
    setErrorToastMessage(message);
    setErrorToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accountId && email && code) {
      triggerSignupConfirmation({ email, code });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, email, code]);

  return (
    <>
      <FullscreenLoadingSpinner />
      <LoadingText>
        {errorToast ? (
          /* istanbul ignore next */
          <ErrorText>{`An error occured confirming your account: ${errorToastMessage}`}</ErrorText>
        ) : (
          `Confirming your account. ${!email || !code ? 'No username or code found, please request a new link' : ''}`
        )}
      </LoadingText>
      <Toast
        dependency={errorToast}
        severity="error"
        title="Error confirming your account"
        message={errorToastMessage ?? ''}
      />
    </>
  );
}

const LoadingText = styled('div')`
  position: absolute;
  top: 60%;
  width: 100%;
  text-align: center;
`;

const ErrorText = styled(MUITypography)`
  width: 100%;
  text-align: center;
  font-weight: bold;
`;
